import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { completeProfile } from '../services/api';
import LanguageSelector from './LanguageSelector';

export default function ProfileCompletePage({ user, onComplete, onSwitchAccount, onSkip }) {
  const ink = '#14214d';
  const [step, setStep] = useState(1); // 1: basics, 2: location, 3: personality, 4: preferences
  const [fullName, setFullName] = useState(user?.name || '');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [country, setCountry] = useState('');
  const [timezone, setTimezone] = useState('');
  const [languages, setLanguages] = useState(['']);
  const [preferredLanguage, setPreferredLanguage] = useState('en');
  const [familyRole, setFamilyRole] = useState('');
  // Personality questions
  const [stressResponse, setStressResponse] = useState('');
  const [decisionStyle, setDecisionStyle] = useState('');
  const [socialEnergy, setSocialEnergy] = useState('');
  const [conflictStyle, setConflictStyle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-detect timezone
  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setTimezone(tz);
    } catch (e) {
      setTimezone('America/Los_Angeles');
    }
  }, []);

  // Try to get country from browser
  useEffect(() => {
    try {
      const locale = navigator.language || navigator.languages[0];
      const countryCode = locale.split('-')[1];
      if (countryCode) {
        const countryNames = {
          'US': 'United States', 'GB': 'United Kingdom', 'CA': 'Canada',
          'AU': 'Australia', 'IN': 'India', 'DE': 'Germany', 'FR': 'France',
          'ES': 'Spain', 'IT': 'Italy', 'BR': 'Brazil', 'MX': 'Mexico',
          'JP': 'Japan', 'CN': 'China', 'KR': 'South Korea',
        };
        setCountry(countryNames[countryCode] || '');
      }
    } catch (e) {}
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step < 4) {
      setStep(step + 1);
      return;
    }
    
    // Final submit
    if (!fullName || !dateOfBirth || !gender || !city || !country) {
      setError('Please fill in all required fields');
      return;
    }
    
    // Parse name into first and last
    const nameParts = fullName.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    setLoading(true);
    setError('');
    try {
      const updated = await completeProfile({
        name: fullName,
        firstName,
        lastName,
        dateOfBirth,
        birthMonth: new Date(dateOfBirth).getMonth() + 1,
        birthYear: new Date(dateOfBirth).getFullYear(),
        age: calculateAge(dateOfBirth),
        gender,
        phone,
        location: `${city}${stateProvince ? ', ' + stateProvince : ''}, ${country}`,
        city,
        stateProvince,
        country,
        timezone,
        languages: languages.filter(l => l.trim()),
        familyRole,
        // Personality traits
        personality: {
          stressResponse,
          decisionStyle,
          socialEnergy,
          conflictStyle,
        },
      });
      onComplete(updated);
    } catch (err) {
      setError(err.message || 'Failed to save profile');
    }
    setLoading(false);
  };

  const calculateAge = (dob) => {
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const addLanguage = () => {
    if (languages.length < 5) {
      setLanguages([...languages, '']);
    }
  };

  const updateLanguage = (index, value) => {
    const newLangs = [...languages];
    newLangs[index] = value;
    setLanguages(newLangs);
  };

  const inputStyle = {
    padding: '16px',
    borderRadius: 16,
    background: 'rgba(255,255,255,0.32)',
    border: '1px solid rgba(255,255,255,0.42)',
    color: ink,
    fontSize: 16,
    outline: 'none',
    backdropFilter: 'blur(12px)',
    width: '100%',
    boxSizing: 'border-box',
  };

  const selectStyle = {
    ...inputStyle,
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2314214d' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 16px center',
    paddingRight: '40px',
  };

  const labelStyle = {
    color: 'rgba(20,33,77,0.85)',
    fontSize: 13,
    marginLeft: 4,
    marginBottom: 6,
    display: 'block',
  };

  const optionCardStyle = (selected) => ({
    padding: '14px 16px',
    borderRadius: 12,
    background: selected ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.2)',
    border: selected ? '2px solid rgba(20,33,77,0.6)' : '1px solid rgba(255,255,255,0.3)',
    color: ink,
    fontSize: 14,
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'left',
  });

  const stepTitles = {
    1: { title: "Let's get to know you", subtitle: "Basic information to personalize your experience" },
    2: { title: "Where are you?", subtitle: "Helps us understand your context and culture" },
    3: { title: "How do you tick?", subtitle: "Helps OiMy adapt to your style (pick what feels closest)" },
    4: { title: "A few more details", subtitle: "Optional but helps OiMy know you better" },
  };

  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      zIndex: 10,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: 24,
      overflowY: 'auto',
    }}>
      <motion.div
        data-testid="profile-complete-page"
        style={{
          padding: '32px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          background: 'rgba(255,255,255,0.12)',
          backdropFilter: 'blur(22px)',
          WebkitBackdropFilter: 'blur(22px)',
          border: '1px solid rgba(255,255,255,0.42)',
          borderRadius: 28,
          boxShadow: '0 16px 38px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.46)',
          maxWidth: 440,
          margin: '0 auto',
          width: '100%',
        }}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <h1 style={{ 
            fontFamily: "'Dreaming Outloud Pro', 'Syne', sans-serif",
            fontSize: 24, 
            fontWeight: 700, 
            color: ink,
            marginBottom: 8,
            textShadow: '0 2px 12px rgba(255,255,255,0.36)'
          }}>
            {stepTitles[step].title}
          </h1>
          <p style={{ color: 'rgba(20,33,77,0.7)', fontSize: 14 }}>
            {stepTitles[step].subtitle}
          </p>
          {/* Progress dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
            {[1, 2, 3, 4].map(s => (
              <div key={s} style={{
                width: 8, height: 8, borderRadius: '50%',
                background: s === step ? ink : 'rgba(20,33,77,0.2)',
                transition: 'all 0.3s',
              }} />
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          
          {/* Step 1: Basics */}
          {step === 1 && (
            <>
              <div>
                <label style={labelStyle}>Full Name *</label>
                <input
                  data-testid="profile-name"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  placeholder="First and Last Name"
                  style={inputStyle}
                  required
                />
                <p style={{ fontSize: 11, color: 'rgba(20,33,77,0.5)', marginTop: 4, marginLeft: 4 }}>
                  Your full name helps us understand your cultural background
                </p>
              </div>

              <div>
                <label style={labelStyle}>Date of Birth *</label>
                <input
                  data-testid="profile-dob"
                  type="date"
                  value={dateOfBirth}
                  onChange={e => setDateOfBirth(e.target.value)}
                  style={inputStyle}
                  required
                />
                <p style={{ fontSize: 11, color: 'rgba(20,33,77,0.5)', marginTop: 4, marginLeft: 4 }}>
                  For birthday reminders & understanding your generation's context
                </p>
              </div>

              <div>
                <label style={labelStyle}>Gender *</label>
                <select
                  data-testid="profile-gender"
                  value={gender}
                  onChange={e => setGender(e.target.value)}
                  style={selectStyle}
                  required
                >
                  <option value="" disabled>Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="non-binary">Non-binary</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                </select>
              </div>

              <div>
                <label style={labelStyle}>Phone Number</label>
                <input
                  data-testid="profile-phone"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  style={inputStyle}
                />
              </div>
            </>
          )}

          {/* Step 2: Location */}
          {step === 2 && (
            <>
              <div>
                <label style={labelStyle}>City *</label>
                <input
                  data-testid="profile-city"
                  value={city}
                  onChange={e => setCity(e.target.value)}
                  placeholder="San Francisco"
                  style={inputStyle}
                  required
                />
              </div>

              <div>
                <label style={labelStyle}>State / Province</label>
                <input
                  data-testid="profile-state"
                  value={stateProvince}
                  onChange={e => setStateProvince(e.target.value)}
                  placeholder="California"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Country *</label>
                <input
                  data-testid="profile-country"
                  value={country}
                  onChange={e => setCountry(e.target.value)}
                  placeholder="United States"
                  style={inputStyle}
                  required
                />
              </div>

              <div>
                <label style={labelStyle}>Timezone</label>
                <select
                  data-testid="profile-timezone"
                  value={timezone}
                  onChange={e => setTimezone(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Auto-detect ({timezone || 'detecting...'})</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="America/Phoenix">Arizona (MST)</option>
                  <option value="Europe/London">London (GMT/BST)</option>
                  <option value="Europe/Paris">Central Europe (CET)</option>
                  <option value="Asia/Dubai">Dubai (GST)</option>
                  <option value="Asia/Kolkata">India (IST)</option>
                  <option value="Asia/Singapore">Singapore (SGT)</option>
                  <option value="Asia/Tokyo">Tokyo (JST)</option>
                  <option value="Australia/Sydney">Sydney (AEST)</option>
                </select>
              </div>
            </>
          )}

          {/* Step 3: Personality Questions */}
          {step === 3 && (
            <>
              <div>
                <label style={labelStyle}>When stressed, I usually...</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { value: 'talk', label: '💬 Talk it out with someone' },
                    { value: 'alone', label: '🧘 Need space to process alone' },
                    { value: 'action', label: '⚡ Take action to fix it immediately' },
                    { value: 'distract', label: '🎮 Distract myself until it passes' },
                  ].map(opt => (
                    <div
                      key={opt.value}
                      onClick={() => setStressResponse(opt.value)}
                      style={optionCardStyle(stressResponse === opt.value)}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label style={labelStyle}>When making decisions, I tend to...</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { value: 'gut', label: '🎯 Trust my gut and decide quickly' },
                    { value: 'research', label: '📊 Research thoroughly before choosing' },
                    { value: 'consult', label: '👥 Ask trusted people for input' },
                    { value: 'delay', label: '⏳ Wait and see how things unfold' },
                  ].map(opt => (
                    <div
                      key={opt.value}
                      onClick={() => setDecisionStyle(opt.value)}
                      style={optionCardStyle(decisionStyle === opt.value)}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label style={labelStyle}>Social situations...</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { value: 'energize', label: '🎉 Energize me — I love being around people' },
                    { value: 'drain', label: '🔋 Drain me — I need alone time to recharge' },
                    { value: 'selective', label: '🎭 Depends on the people and context' },
                  ].map(opt => (
                    <div
                      key={opt.value}
                      onClick={() => setSocialEnergy(opt.value)}
                      style={optionCardStyle(socialEnergy === opt.value)}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label style={labelStyle}>When there's conflict, I...</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { value: 'direct', label: '🗣️ Address it directly and openly' },
                    { value: 'avoid', label: '🕊️ Prefer to avoid confrontation' },
                    { value: 'mediate', label: '🤝 Try to find middle ground' },
                    { value: 'process', label: '💭 Need time to process before responding' },
                  ].map(opt => (
                    <div
                      key={opt.value}
                      onClick={() => setConflictStyle(opt.value)}
                      style={optionCardStyle(conflictStyle === opt.value)}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Step 4: Preferences */}
          {step === 4 && (
            <>
              <div>
                <label style={labelStyle}>Languages Spoken</label>
                {languages.map((lang, i) => (
                  <input
                    key={i}
                    data-testid={`profile-language-${i}`}
                    value={lang}
                    onChange={e => updateLanguage(i, e.target.value)}
                    placeholder={i === 0 ? "English" : "Add another language"}
                    style={{ ...inputStyle, marginBottom: 8 }}
                  />
                ))}
                {languages.length < 5 && (
                  <button
                    type="button"
                    onClick={addLanguage}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'rgba(20,33,77,0.6)',
                      fontSize: 13,
                      cursor: 'pointer',
                      padding: '4px 0',
                    }}
                  >
                    + Add another language
                  </button>
                )}
              </div>

              <div>
                <label style={labelStyle}>Your Role in the Family</label>
                <select
                  data-testid="profile-family-role"
                  value={familyRole}
                  onChange={e => setFamilyRole(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Select role (optional)</option>
                  <option value="self">Primary Account Holder</option>
                  <option value="spouse">Spouse / Partner</option>
                  <option value="parent">Parent</option>
                  <option value="child">Child</option>
                  <option value="grandparent">Grandparent</option>
                  <option value="sibling">Sibling</option>
                  <option value="other">Other Family Member</option>
                </select>
              </div>

              <p style={{ fontSize: 12, color: 'rgba(20,33,77,0.5)', textAlign: 'center', marginTop: 8 }}>
                OiMy uses this to understand your cultural background, communication style, 
                and how to best support you and your family.
              </p>
            </>
          )}

          {error && (
            <div style={{ color: '#ff6b6b', fontSize: 13, textAlign: 'center', background: 'rgba(255,0,0,0.1)', padding: 8, borderRadius: 8 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                style={{
                  flex: 1,
                  padding: 16,
                  borderRadius: 16,
                  background: 'rgba(255,255,255,0.2)',
                  color: ink,
                  border: '1px solid rgba(255,255,255,0.3)',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Back
              </button>
            )}
            <motion.button
              data-testid="profile-submit-btn"
              type="submit"
              disabled={loading}
              style={{
                flex: 2,
                padding: 16,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #fff 0%, #ddd 100%)',
                color: ink,
                border: 'none',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                opacity: loading ? 0.7 : 1,
                boxShadow: '0 4px 12px rgba(255,255,255,0.15)',
              }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? 'Saving...' : (step < 4 ? 'Continue' : 'Get Started')}
            </motion.button>
          </div>
        </form>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 4 }}>
          <button
            type="button"
            onClick={onSwitchAccount}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.78)',
              textShadow: '0 1px 0 rgba(255,255,255,0.28)',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Use another account
          </button>
          <button
            type="button"
            onClick={onSkip}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.78)',
              textShadow: '0 1px 0 rgba(255,255,255,0.28)',
              fontSize: 12,
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Skip for now
          </button>
        </div>
      </motion.div>
    </div>
  );
}
